import React, {useCallback, useEffect, useMemo} from 'react';
import {createVenue, getVenues} from '../../api/adminAPI';
import {Venue} from '../../interfaces';
import {
  Alert,
  Box,
  Button,
  IconButton,
  InputAdornment,
  Modal,
  TextField,
  Typography,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import PageTitle from '../shared/PageTitle';
import {useTranslation} from 'react-i18next';
import {Helmet} from 'react-helmet';
import VenuesTable from './VenuesTable';
import styles from '../../styles';
import {Control, FieldValues, useForm} from 'react-hook-form';
import FormTextField from '../shared/FormTextField';

interface Props {}

type FormValues = {
  name: string;
  country: string;
  stateOrProvince: string;
  address: string;
  city: string;
  lat: string;
  lon: string;
};

const AdminVenues: React.FC<Props> = () => {
  const {handleSubmit, reset, control} = useForm<FormValues>({
    defaultValues: {
      name: '',
      country: '',
      stateOrProvince: '',
      address: '',
      city: '',
      lat: '',
      lon: '',
    },
    mode: 'onBlur',
  });
  const {t} = useTranslation();

  const [venues, setVenues] = React.useState<Venue[]>([]);
  const [showAddDialog, setShowAddDialog] = React.useState(false);
  const [pending, setPending] = React.useState(true);
  const [submitting, setSubmitting] = React.useState(false);
  const [searchTerm, setSearchTerm] = React.useState('');
  const [formError, setFormError] = React.useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setPending(true);
    const venueData = await getVenues();
    setVenues(venueData);
    setPending(false);
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAddDialogClick = useCallback(() => {
    setShowAddDialog(true);
  }, []);

  const handleAddDialogClose = useCallback(() => {
    reset();
    setSubmitting(false);
    setFormError(null);
    setShowAddDialog(false);
  }, [reset]);

  const handleAddSubmit = useCallback(
    async (data: FormValues) => {
      setFormError(null);
      const hasLat = data.lat && data.lat.trim() !== '';
      const hasLon = data.lon && data.lon.trim() !== '';

      // Validate lat/lon are valid numbers
      if (hasLat && hasLon) {
        const latNum = parseFloat(data.lat);
        const lonNum = parseFloat(data.lon);

        if (isNaN(latNum) || latNum < -90 || latNum > 90) {
          setFormError(t('admin.invalidLatitude') as string);
          return;
        }

        if (isNaN(lonNum) || lonNum < -180 || lonNum > 180) {
          setFormError(t('admin.invalidLongitude') as string);
          return;
        }
      }

      setSubmitting(true);
      try {
        await createVenue({
          name: data.name,
          country: data.country,
          stateOrProvince: data.stateOrProvince,
          address: data.address || undefined,
          city: data.city || undefined,
          lat: data.lat ? parseFloat(data.lat) : undefined,
          lon: data.lon ? parseFloat(data.lon) : undefined,
        });
        fetchData();
        handleAddDialogClose();
      } catch (error) {
        setFormError(t('admin.venueCreateFailed') as string);
        setSubmitting(false);
      }
    },
    [fetchData, handleAddDialogClose, t],
  );

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(e.target.value);
    },
    [],
  );

  const handleClearSearch = useCallback(() => {
    setSearchTerm('');
  }, []);

  const filteredVenues = useMemo(() => {
    if (!searchTerm.trim()) {
      return venues;
    }
    const lowerSearch = searchTerm.toLowerCase();
    return venues.filter(
      venue =>
        venue.name?.toLowerCase().includes(lowerSearch) ||
        venue.city?.toLowerCase().includes(lowerSearch) ||
        venue.stateOrProvince?.toLowerCase().includes(lowerSearch) ||
        venue.country?.toLowerCase().includes(lowerSearch) ||
        venue.address?.toLowerCase().includes(lowerSearch),
    );
  }, [venues, searchTerm]);

  return (
    <>
      <Helmet>
        <title>
          {t('navigation.pageTitle', {pageName: t('pages.venues')})}
        </title>
      </Helmet>
      <Box>
        <PageTitle>{t('pages.venues')}</PageTitle>
        <Box sx={{display: 'flex', gap: 2, mb: 2, alignItems: 'center'}}>
          <Button onClick={handleAddDialogClick}>
            {t('admin.createVenue')}
          </Button>
          <TextField
            size="small"
            placeholder={t('admin.searchVenues') as string}
            value={searchTerm}
            onChange={handleSearchChange}
            sx={{minWidth: 300}}
            InputProps={{
              endAdornment: searchTerm ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={handleClearSearch}
                    edge="end"
                    aria-label={t('general.clear') as string}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
        </Box>
        <Modal
          open={showAddDialog}
          onClose={handleAddDialogClose}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description">
          <Box sx={styles.modal}>
            <Typography variant="h4" sx={{mb: 2}}>
              {t('admin.addVenue')}
            </Typography>
            {formError && (
              <Alert severity="error" sx={{mb: 2}}>
                {formError}
              </Alert>
            )}
            <form onSubmit={handleSubmit(handleAddSubmit)} className="form">
              <FormTextField
                control={control as unknown as Control<FieldValues>}
                name="name"
                label={t('general.name') as string}
                required
              />
              <FormTextField
                control={control as unknown as Control<FieldValues>}
                name="country"
                label={t('admin.country') as string}
                required
              />
              <FormTextField
                control={control as unknown as Control<FieldValues>}
                name="stateOrProvince"
                label={t('admin.stateOrProvince') as string}
                required
              />
              <FormTextField
                control={control as unknown as Control<FieldValues>}
                name="city"
                label={t('admin.city') as string}
                required
              />
              <FormTextField
                control={control as unknown as Control<FieldValues>}
                name="address"
                label={t('admin.address') as string}
                required
              />
              <FormTextField
                control={control as unknown as Control<FieldValues>}
                name="lat"
                label={t('admin.latitude') as string}
                required
              />
              <FormTextField
                control={control as unknown as Control<FieldValues>}
                name="lon"
                label={t('admin.longitude') as string}
                required
              />
              <Button disabled={submitting} type="submit">
                {t('general.submit')}
              </Button>
            </form>
          </Box>
        </Modal>
        <VenuesTable venues={filteredVenues ?? []} loading={pending} />
      </Box>
    </>
  );
};

export default React.memo(AdminVenues);
